import { observable, runInAction, action, computed } from 'mobx';
import MongoConsoleService from '../services/MongoConsoleService';
import message from 'antd/lib/message';

export default class MongoConsoleStore {
    @observable
    databases: string[] = [];

    @observable
    collections: string[] = [];

    @observable
    selectedDatabase: string | undefined = undefined;

    @observable
    selectedCollection: string | undefined = undefined;

    @observable
    filter: string = '{}';

    @observable
    projection: string = '';

    @observable
    limit: number = 100;

    @observable
    isLoadingDatabases: boolean = false;

    @observable
    isLoadingCollections: boolean = false;

    @observable
    isExecuting: boolean = false;

    @observable
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    results: Record<string, unknown>[] = [];

    @observable
    columns: string[] = [];

    @observable
    documentCount: number = 0;

    @observable
    executionTimeMs: number = 0;

    @observable
    executionError: string | null = null;

    private collectionsRequestId: number = 0;

    @computed
    get hasResults() {
        return this.results.length > 0;
    }

    @computed
    get canExecute() {
        return !!this.selectedDatabase && !!this.selectedCollection && !!this.filter && !this.isExecuting;
    }

    constructor(private service: MongoConsoleService) {}

    @action.bound
    async loadDatabases() {
        try {
            runInAction(() => {
                this.isLoadingDatabases = true;
            });

            const data = await this.service.getDatabases();

            runInAction(() => {
                this.databases = data;
            });
        } catch (err) {
            console.error(err);
            message.error('Failed to load databases');
        } finally {
            runInAction(() => {
                this.isLoadingDatabases = false;
            });
        }
    }

    @action.bound
    async loadCollections(database: string) {
        const requestId = ++this.collectionsRequestId;
        try {
            runInAction(() => {
                this.isLoadingCollections = true;
                this.collections = [];
                this.selectedCollection = undefined;
            });

            const data = await this.service.getCollections(database);

            // Discard stale response if user switched databases while loading
            if (requestId !== this.collectionsRequestId) {
                return;
            }

            runInAction(() => {
                this.collections = data;
            });
        } catch (err) {
            if (requestId !== this.collectionsRequestId) {
                return;
            }
            console.error(err);
            message.error('Failed to load collections');
        } finally {
            if (requestId === this.collectionsRequestId) {
                runInAction(() => {
                    this.isLoadingCollections = false;
                });
            }
        }
    }

    @action.bound
    async executeQuery() {
        if (!this.selectedDatabase || !this.selectedCollection) {
            message.warning('Please select a database and collection');
            return;
        }

        try {
            runInAction(() => {
                this.isExecuting = true;
                this.executionError = null;
                this.results = [];
                this.columns = [];
                this.documentCount = 0;
                this.executionTimeMs = 0;
            });

            const resp = await this.service.executeQuery({
                database: this.selectedDatabase,
                collection: this.selectedCollection,
                filter: this.filter,
                projection: this.projection || undefined,
                limit: this.limit
            });

            if (resp.isOk()) {
                const result = resp.value;
                let parsedData: Record<string, unknown>[] = [];

                if (typeof result.data === 'string') {
                    try {
                        parsedData = JSON.parse(result.data);
                    } catch {
                        parsedData = [];
                    }
                } else if (Array.isArray(result.data)) {
                    parsedData = result.data;
                }

                // Extract unique column names from all documents
                const columnSet = new Set<string>();
                for (const doc of parsedData) {
                    for (const key of Object.keys(doc)) {
                        columnSet.add(key);
                    }
                }

                runInAction(() => {
                    this.results = parsedData;
                    this.columns = Array.from(columnSet);
                    this.documentCount = result.documentCount;
                    this.executionTimeMs = result.executionTimeMs;
                });
            } else {
                const errorMessage = resp.error?.data?.title || 'Failed to execute query';
                runInAction(() => {
                    this.executionError = errorMessage;
                });
                message.error(errorMessage);
            }
        } catch (err) {
            console.error(err);
            message.error('Failed to execute query');
        } finally {
            runInAction(() => {
                this.isExecuting = false;
            });
        }
    }

    @action.bound
    setSelectedDatabase(database: string | undefined) {
        this.selectedDatabase = database;
        if (database) {
            this.loadCollections(database);
        } else {
            this.collections = [];
            this.selectedCollection = undefined;
        }
    }

    @action.bound
    setSelectedCollection(collection: string | undefined) {
        this.selectedCollection = collection;
    }

    @action.bound
    setFilter(filter: string) {
        this.filter = filter;
    }

    @action.bound
    setProjection(projection: string) {
        this.projection = projection;
    }

    @action.bound
    setLimit(limit: number) {
        this.limit = limit;
    }

    @action.bound
    clearResults() {
        this.results = [];
        this.columns = [];
        this.documentCount = 0;
        this.executionTimeMs = 0;
        this.executionError = null;
    }
}
